import { useCallback, useEffect, useState } from "react";
import { Store, RefreshCw, Loader2, UploadCloud, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ShopThemePatch } from "./ShopThemePatch";
import { ShopOrdersPanel } from "./ShopOrdersPanel";
import api from "../lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

const HeroPreview = ({ hero, busy, onRebuild }) => (
  <div className="space-y-3">
    <div className="rounded-2xl overflow-hidden border border-border bg-[#f6f4ef] aspect-[4/3] grid place-items-center">
      {hero ? (
        <img data-testid="shop-hero-preview" src={hero.url} alt="Custom Duck hero collage" className="h-full w-full object-cover" />
      ) : (
        <p data-testid="shop-hero-empty" className="text-sm text-muted-foreground px-6 text-center">No collage yet — build one from your finished ducks.</p>
      )}
    </div>
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs text-muted-foreground font-mono2">{hero ? `${hero.count} ducks · ${hero.width}×${hero.height} · ${when(hero.at)}` : "—"}</p>
      <Button data-testid="shop-hero-regenerate-btn" variant="outline" size="sm" className="rounded-full gap-2" onClick={onRebuild} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {hero ? "Shuffle collage" : "Build collage"}
      </Button>
    </div>
  </div>
);

const SyncStatus = ({ last, error }) => {
  if (error) return <p data-testid="shop-sync-error" className="text-sm text-destructive">{error}</p>;
  if (!last) return <p data-testid="shop-sync-status" className="text-xs text-muted-foreground">Not synced yet.</p>;
  return (
    <p data-testid="shop-sync-status" className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
      <span>Synced {when(last.at)} · image {String(last.media_status || "").toLowerCase()}</span>
      <a data-testid="shop-product-link" href={last.product_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline hover:text-foreground">
        Product page <ExternalLink className="h-3 w-3" />
      </a>
      <a data-testid="shop-admin-link" href={last.admin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline hover:text-foreground">
        Shopify admin <ExternalLink className="h-3 w-3" />
      </a>
    </p>
  );
};

// Admin card: the Custom Duck product's hero collage + description, pushed to Shopify through the Admin API.
export const ShopProductCard = () => {
  const [info, setInfo] = useState(null);
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/library/shop", { params: { public_base: window.location.origin } });
    setInfo(data);
    setHtml((h) => h || data.draft_description || "");
  }, []);

  useEffect(() => {
    load().catch(() => setInfo({ configured: false }));
  }, [load]);

  if (!info?.configured) return null;

  const rebuild = async () => {
    setBusy("hero");
    try {
      await api.post("/library/shop/hero");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the collage");
    }
    setBusy("");
  };

  const importRefs = async () => {
    setBusy("refs");
    try {
      await api.post("/library/shop/refs");
      await load();
      const poll = setInterval(async () => {
        const { data } = await api.get("/library/shop", { params: { public_base: window.location.origin } });
        setInfo(data);
        if (data.refs?.status !== "running") clearInterval(poll);
      }, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the import");
    }
    setBusy("");
  };

  const sync = async () => {
    setBusy("sync");
    setError("");
    try {
      const { data } = await api.post("/library/shop/sync", { description_html: html, public_base: window.location.origin });
      setInfo((i) => ({ ...i, last_sync: data }));
      toast.success(`Shopify updated — image ${String(data.media_status).toLowerCase()}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Sync failed");
    }
    setBusy("");
  };

  return (
    <section data-testid="shop-product-card" className="rounded-3xl border border-border bg-card/60 p-5 space-y-4">
      <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground">
        <Store className="h-4 w-4" /> Shop product · {info.product?.title || "Custom Duck"} on {info.store}
      </p>
      <div className="grid lg:grid-cols-2 gap-6">
        <HeroPreview hero={info.hero} busy={busy === "hero"} onRebuild={rebuild} />
        <div className="space-y-3">
          <textarea
            data-testid="shop-description-input"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            spellCheck={false}
            className="w-full h-56 rounded-2xl border border-border bg-background p-3 text-xs font-mono2 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <Button data-testid="shop-sync-btn" onClick={sync} disabled={busy !== "" || !info.hero || !html.trim() || !info.admin_configured} className="w-full h-12 rounded-full font-bold gap-2">
            {busy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Sync image + description to Shopify
          </Button>
          {!info.admin_configured && <p className="text-xs text-destructive">Admin API not configured — set SHOPIFY_ADMIN_DOMAIN and SHOPIFY_CLIENT_ID / SHOPIFY_CLIENT_SECRET in backend/.env.</p>}
          <SyncStatus last={info.last_sync} error={error} />
          <p className="text-xs text-muted-foreground">Keeps your existing product photos; the collage becomes the featured image and the sizes/prices in the text come from the live variants. After the first sync, the collage reshuffles itself (at most hourly) whenever a new duck finishes.</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ShopThemePatch theme={info.theme} onChanged={load} />
        <ShopOrdersPanel orders={info.orders} onChanged={load} />
      </div>
      <div data-testid="shop-refs-panel" className="rounded-2xl border border-border bg-background/60 p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground"><Store className="h-4 w-4" /> Product photos as render references</p>
          <p data-testid="shop-refs-status" className="text-xs text-muted-foreground mt-1">
            {info.refs?.status === "running"
              ? `Importing… ${info.refs.done || 0}/${info.refs.total || "?"}`
              : info.refs?.count
                ? `${info.refs.count} real printed-duck photos imported · ${new Date(info.refs.at).toLocaleString()} — 2 are shown to Gemini with every new render`
                : "Not imported yet — new renders only see library ducks."}
            {info.refs?.error && <span className="text-destructive"> · {info.refs.error}</span>}
          </p>
        </div>
        <Button data-testid="shop-refs-import-btn" variant="outline" size="sm" className="rounded-full gap-2" disabled={busy !== "" || info.refs?.status === "running"} onClick={importRefs}>
          {busy === "refs" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {info.refs?.count ? "Re-import photos" : "Import product photos"}
        </Button>
      </div>
    </section>
  );
};
